// Das Ufer (§6): Kopfleiste, Bank und Seitenleiste mit Infos und Verlauf jeder Figur.
// Aufgeteilt aus shore.ts.
import {
  combatRules,
  defaultKniff,
  fillNumbers,
  getAbility,
  itemLabel,
  itemText,
  getClass,
  getEnemy,
  getSpecies,
  getTrait,
  lineups,
  systemOn,
  enemyKniff,
  viaName,
  viaPhrase,
  traitOfEntry,
} from '../../content/index.ts';
import { stormlingStats, type Unit } from '../../sim/index.ts';
import { CLASS_COLORS } from '../render.ts';
import { figuresWanted, speedIcon, speedLabel } from '../art.ts';
import { enemyLabel, enemyNames } from '../summary.ts';
import { rangeWord, speedWord } from '../words.ts';
import type { Member, Info } from './state.ts';
import {
  $,
  esc,
  BEHAVIOR_TEXT,
  fmt,
  stormName,
  member,
  statsOf,
  hpOf,
  S,
  stormLevel,
  enemyLabels,
  view,
  maxOnField,
  bench,
  retreatIds,
  currentFrame,
} from './state.ts';

export function renderControls(): void {
  const lineupSel = $<HTMLSelectElement>('lineup');
  if (!lineupSel.options.length) {
    lineupSel.innerHTML = lineups.map((l, i) => `<option value="${i}">${l.difficulty} · ${esc(l.name)}</option>`).join('');
    $<HTMLSelectElement>('storm').innerHTML = combatRules.stormLevels.map((l) => `<option value="${l.id}">${esc(l.name)}</option>`).join('');
  }
  // I26: Die Auswahl hängt vom Run ab (Hasenfuß), darum jedes Mal neu
  const allowed = retreatIds();
  const retreatSel = $<HTMLSelectElement>('retreat');
  const wanted = combatRules.retreatThresholds.filter((r) => allowed.includes(r.id) || r.id === S.retreat).map((r) => `<option value="${r.id}">${esc(r.name)}</option>`).join('');
  if (retreatSel.innerHTML !== wanted) retreatSel.innerHTML = wanted;
  lineupSel.value = String(S.lineupIdx);
  $<HTMLSelectElement>('storm').value = S.storm;
  $<HTMLSelectElement>('retreat').value = S.retreat;
  // K17: Am Durchlass gibt es kein Zurück. K34 (Phase Kern): Ohne Wahl des Rückzugs ist die Auswahl ausgeblendet
  $('retreat-label').hidden = !systemOn('retreatChoice') || !systemOn('retreat');
  $<HTMLSelectElement>('retreat').disabled = !!S.run?.noRetreat;
  $('retreat-label').title = S.run?.noRetreat ? 'Am Durchlass gibt es kein Zurück.' : '';
  $('slots-value').textContent = `${S.placed.size}/${maxOnField()}`;
  $('onfield').textContent = S.mode === 'run' ? `${S.roster.length - S.placed.size} noch im Beiboot` : `${S.roster.length - S.placed.size} an Bord`;
  $('bench-label').textContent = S.mode === 'run' ? 'Landtrupp' : 'An Bord';
  for (const b of document.querySelectorAll<HTMLButtonElement>('[data-speed]')) {
    const v = Number(b.dataset.speed);
    const on = v === S.speed;
    b.classList.toggle('active', on);
    // Runde 8: das Zeichen statt der Schrift; die Schrift bleibt als Hinweis und für Vorleseprogramme
    const html = speedIcon(v, on);
    if (b.innerHTML !== html) b.innerHTML = html;
    b.title = speedLabel(v);
    b.setAttribute('aria-label', `Tempo ${speedLabel(v)}`);
  }
}

export function renderBench(): void {
  const list = bench();
  $('bench-list').innerHTML = list.length
    ? list
        .map((key) => {
          const m = member(key);
          const cls = m.friendClass;
          const hp = S.mode === 'run' ? ` <small>${Math.round((100 * hpOf(m)) / statsOf(m).hp)} %</small>` : '';
          return `<button class="chip" data-key="${key}" title="${esc(getClass(cls).name)}"><span class="dot" style="background:${CLASS_COLORS[cls]}"></span>${esc(m.name)}${hp}</button>`;
        })
        .join('')
    : `<span class="empty">${S.mode === 'run' ? 'Der ganze Landtrupp steht am Ufer.' : 'Alle sind am Ufer.'}</span>`;
}

export function infoHtml(info: Info | null): string {
  if (!info) return '';
  if (info.kind === 'tree') {
    return `<h3>Baum des Eichhörnchens</h3><p>Ein Objekt, das man mit aufstellt. Solange er steht, sitzt das Eichhörnchen oben und kann nicht angegriffen werden, nur der Baum.</p>`;
  }
  if (info.kind === 'friend' || info.infested) {
    const sp = getSpecies(info.id);
    const m: Member | undefined = info.member ?? (info.infested ? { key: '', species: info.infested.species, friendClass: info.infested.friendClass, name: info.infested.name, profile: info.infested.profile, level: info.infested.level } : undefined);
    const clsId = m?.friendClass ?? (info.unit?.friendClass ?? sp.classes[0]!.friendClass);
    const entry = sp.classes.find((c) => c.friendClass === clsId) ?? sp.classes[0]!;
    const cls = getClass(clsId);
    const st = m ? statsOf(m) : cls.stats;
    const passive = getAbility(sp.passive);
    const kniff = getAbility(m?.profile?.kniff ?? defaultKniff(entry)!);
    const u = info.unit;
    const heal = m?.profile?.attackHeal ?? entry.attackHeal;
    const hpNow = u ? u.hp : m ? hpOf(m) : null;
    const who = m && S.mode === 'run' ? `${esc(m.name)} <small>${esc(sp.name)}, ${esc(cls.name)}${m.level ? `, Level ${m.level}` : ''}</small>` : `${esc(sp.name)} <small>${esc(cls.name)}</small>`;
    // Fabi (Phase Kern): Beim Aufstellen sehen, was ein Freund durch Ast, Stufen und Items kann, mit Wirkung
    const items = m?.items?.length
      ? `<p class="label">Items</p><ul class="traits">${m.items.map((i) => `<li><b>${esc(itemLabel(i))}:</b> ${esc(itemText(i))}</li>`).join('')}</ul>`
      : '';
    const way = m?.path?.length
      ? `<p class="label">Weg</p><ul class="traits">${m.path.map((p) => `<li><b>${esc(p.name)}</b> <small>Level ${p.level}${p.branch ? ', Ast' : ''}</small>: ${esc(p.text)}</li>`).join('')}</ul>`
      : S.mode === 'run'
        ? '<p class="muted">Noch keinen Ast gewählt.</p>'
        : '';
    const ct = traitOfEntry(entry);
    const trait = ct ? `<p><b>${esc(ct.name)}</b> (als ${esc(cls.name)}): ${esc(fillNumbers(ct.text, m?.profile?.params, st.damage))}</p>` : '';
    return `<h3>${info.infested ? `${esc(info.infested.name)} <small>befallen: ${esc(sp.name)}, ${esc(cls.name)}, Level ${info.infested.level}</small>` : who}</h3>
      ${info.infested ? `<p class="muted">Eine Sturmklette hat sich an ${esc(info.infested.name)} festgeklettet und kontrolliert jetzt jeden Schritt. Gewinnt ihr, ist ${esc(info.infested.name)} frei und reist mit euch, wenn ihr wollt.</p>` : ''}
      <dl>
        <dt>Leben</dt><dd>${hpNow !== null ? `${Math.round(hpNow)} / ` : ''}${Math.round(st.hp)}${u && u.shield > 0.5 ? ` <span class="shield-note">+${Math.round(u.shield)} Schild</span>` : ''}</dd>
        ${
          // S5 (Fabi, Abschlusstest Phase 4): Wer mit jedem Angriff heilt, zeigt zuerst die Heilung; Schaden macht er nur, wenn alle gesund sind
          heal
            ? `<dt>Heilt pro Angriff</dt><dd><b>${Math.round(heal)}</b> <small class="muted">wenn ein Freund verletzt ist</small></dd>
        <dt>Schaden</dt><dd>${Math.round(st.damage)} <small class="muted">nur wenn alle gesund sind</small></dd>`
            : `<dt>Schaden</dt><dd>${Math.round(st.damage)}</dd>`
        }
        <dt>Angriffe</dt><dd>${fmt(1 / st.attackInterval)} pro s</dd>
        <dt>Reichweite</dt><dd>${rangeWord(st.range)}</dd>
        <dt>Tempo</dt><dd>${Math.round(u ? u.moveSpeed : st.moveSpeed)}, ${speedWord(u ? u.moveSpeed : st.moveSpeed)}</dd>
        <dt>Volltreffer</dt><dd>${Math.round(st.critChance * 100)} %</dd>
        <dt>Mut</dt><dd>${u ? `${Math.round(liveMut(u))} / ` : ''}${Math.round(combatRules.mut.max * (m?.profile?.perks?.mutNeed ?? entry.mutNeed ?? 1))}${u ? '' : kniff.params.readyAtStart ? ', zu Kampfbeginn voll' : `, startet mit ${Math.round(Math.min(combatRules.mut.startMax, m?.profile?.perks?.startMut ?? entry.startMut ?? 0) * combatRules.mut.max * (m?.profile?.perks?.mutNeed ?? entry.mutNeed ?? 1))}`}</dd>
      </dl>
      ${sp.passive === 'baumhaus' ? `<p class="muted">${(m?.profile?.perks?.treeThrow ?? entry.perks?.treeThrow) ? 'Solange sein Baum steht, sitzt es oben, wirft Nüsse auf Gegner (wenig Schaden) und nutzt seinen Kniff.' : 'Solange sein Baum steht, sitzt es oben und handelt nur mit seinem Kniff.'}</p>` : ''}
      <p><b>${esc(passive.name)}</b> (Passive): ${esc(fillNumbers(passive.text, m?.profile?.params, st.damage))}</p>
      <p><b>${esc(kniff.name)}</b> (Kniff): ${esc(fillNumbers(m?.profile?.kniffText ?? kniff.text, m?.profile?.params, st.damage))}</p>${guardWard(info, kniff.mechanic)}${wounded(m)}${trait}${way}${items}`;
  }
  const def = getEnemy(info.id);
  const kniff = getAbility(enemyKniff(def));
  // Spieltest Phase 4 (Fabi): Im Kampf die echten Werte der Figur, beim Aufstellen dieselbe Rechnung wie im Kampf (Tag, Sturm, Würfel)
  const placed = info.enemyIndex !== undefined ? S.enemies[info.enemyIndex] : undefined;
  const pre = placed ? stormlingStats(placed, stormLevel(), combatRules) : undefined;
  const damage = info.unit ? info.unit.damage : (pre?.damage ?? def.stats.damage);
  const hpMax = info.unit ? info.unit.maxHp : (pre?.maxHp ?? def.stats.hp);
  const interval = info.unit ? info.unit.attackInterval : (pre?.attackInterval ?? def.stats.attackInterval);
  const speed = info.unit ? info.unit.moveSpeed : (pre?.moveSpeed ?? def.stats.moveSpeed);
  const mutMax = info.unit ? info.unit.mutMax : combatRules.mut.max * (def.mutNeed ?? 1);
  const behavior = BEHAVIOR_TEXT[def.behavior] ?? 'Geht auf den nächsten Freund.';
  const traits = info.traits.map((t) => `<li><b>${esc(getTrait(t).name)}:</b> ${esc(getTrait(t).text)}</li>`).join('');
  // G30 (Fabi, Phase Kern): im Kampf steht nur der Spitzname; hier, beim Darüberfahren und Anklicken, auch die Spezies
  const nick = info.unit ? displayName(info.unit.uid) : info.enemyIndex !== undefined ? esc(enemyLabels(S.enemies)[info.enemyIndex] ?? def.name) : esc(def.name);
  return `<h3>${nick !== esc(def.name) ? `${nick} <small>${esc(def.name)}, ein Sturmwesen</small>` : `${esc(def.name)} <small>Sturmwesen</small>`}</h3>
    <p>${behavior}</p>
    <dl>
      <dt>Leben</dt><dd>${info.unit ? `${Math.round(info.unit.hp)} / ` : ''}${Math.round(hpMax)}${info.unit && info.unit.shield > 0.5 ? ` <span class="shield-note">+${Math.round(info.unit.shield)} Schild</span>` : ''}</dd>
      <dt>Schaden</dt><dd>${Math.round(damage)}</dd>
      <dt>Angriffe</dt><dd>${fmt(1 / interval)} pro s</dd>
      <dt>Reichweite</dt><dd>${rangeWord(def.stats.range)}</dd>
      <dt>Tempo</dt><dd>${Math.round(speed)}, ${speedWord(speed)}</dd>
      <dt>Mut</dt><dd>${info.unit ? `${Math.round(liveMut(info.unit))} / ` : ''}${Math.round(mutMax)}</dd>
    </dl>
    <p><b>${esc(kniff.name)}</b> (Kniff): ${esc(fillNumbers(kniff.text, {}, damage))}</p>
    ${traits ? `<ul class="traits">${traits}</ul>` : '<p class="muted">Keine Eigenschaft.</p>'}
    ${info.items?.length ? `<p class="message">Trägt ${info.items.map((i) => `${esc(itemLabel(i))} (${esc(itemText(i))})`).join(' und ')}; das macht ihn stärker.</p>` : ''}`;
}

/**
 * S10 (Fabi, 17.10.2026 nachts: „Wie wählt man die Leibwache?“): Steht ein Freund mit Leibwache am Ufer, sagt das
 * Aufstellen gleich, wie man seinen Schützling wählt
 */
function guardHint(): string {
  const guard = view.setupInfo.find((i) => i.kind === 'friend' && i.ward);
  return guard?.member ? `<p class="message">Leibwache: ${esc(guard.member.name)} passt auf <b>${esc(guard.ward!)}</b> auf. Zieht den goldenen Schild an ${esc(guard.member.name)} auf einen anderen Freund, um ihn umzustellen.</p>` : '';
}

/** B25 (Fabi, 29.10.2026): Wer aus dem Gemeinschaftsraum verbunden ist, steht am besten nebeneinander */
function bondHint(): string {
  const groups = S.mode === 'run' ? S.run?.bonds?.groups ?? [] : [];
  const names = groups
    .map((g) => g.map((id) => S.roster.find((m) => m.key === id)?.name).filter((n): n is string => !!n))
    .filter((n) => n.length >= 2);
  if (!names.length) return '';
  return names.map((n) => `<p class="message bond">♥ ${esc(n.join(' und '))} waren zusammen im Gemeinschaftsraum. Stellt ihr sie nebeneinander, kämpfen sie besser.</p>`).join('');
}

/** S10 Leibwache (Phase Kern): Wen er beschützt und wie man das ändert */
function guardWard(info: Info, mechanic: string | undefined): string {
  if (mechanic !== 'guardian' || info.kind !== 'friend') return '';
  if (S.screen !== 'setup') return '';
  return info.ward
    ? `<p class="message">Schützling: <b>${esc(info.ward)}</b>. Wird er angegriffen, eilt der Igel zu ihm und zieht die Angreifer auf sich. Zum Wechseln den goldenen Schild rechts oben am Igel auf einen anderen Freund ziehen oder T drücken.</p>`
    : '<p class="message">Noch kein Schützling: Stellt jemanden dazu, dann passt der Igel auf ihn auf.</p>';
}

/** F42 (Phase Kern): Die Wunde mit ihrer Folge, in der Info beim Aufstellen */
function wounded(m: Member | undefined): string {
  if (!m?.wounded) return '';
  return `<p class="wound-note">Verwundet: Fällt ${esc(m.name)} heute noch einmal, nimmt ${esc(m.name)} Abschied. Die Wunde heilt über Nacht.</p>`;
}

/** K28 (Fabi, Runde 8): Tempo aller Figuren auf einen Blick, damit man weiß, wie man aufstellt */
export function setupTempo(): { name: string; side: 'friend' | 'enemy'; speed: number }[] {
  const out: { name: string; side: 'friend' | 'enemy'; speed: number }[] = S.roster.map((m) => ({ name: m.name, side: 'friend' as const, speed: statsOf(m).moveSpeed }));
  const names = enemyLabels(S.enemies);
  S.enemies.forEach((e, i) => {
    const speed = e.infested ? e.infested.profile.stats.moveSpeed : stormlingStats(e, stormLevel(), combatRules).moveSpeed!;
    out.push({ name: e.enemy ? enemyLabel(names[i]!, e.enemy) : names[i]!, side: 'enemy', speed });
  });
  return out;
}

export function tempoList(list: { name: string; side: string; speed: number }[]): string {
  if (!list.length) return '';
  const rows = [...list]
    .sort((a, b) => b.speed - a.speed)
    .map((x) => `<li class="${x.side === 'friend' ? 'fr' : 'en'}"><span>${esc(x.name)}</span><b>${Math.round(x.speed)}</b> <small>${speedWord(x.speed)}</small></li>`)
    .join('');
  return `<details class="tempo" open><summary>Tempo, schnellste zuerst</summary><ul class="tempo-list">${rows}</ul></details>`;
}

let lastSidebar = '';

/** Wessen Verlauf zuletzt in der Seitenleiste stand */
let lastLogUid: number | null = null;

export function renderSidebar(): void {
  let html = '';
  if (S.screen === 'setup') {
    const hovered = S.hoverUid !== null ? view.setupInfo[S.hoverUid] ?? null : null;
    html = hovered
      ? infoHtml(hovered)
      : `<h3>Aufstellen${S.mode === 'run' && S.run ? ` <small>${esc(S.run.title)}</small>` : ''}</h3><p>Zieht eure Tiere aufs Ufer. Zeigt auf eine Figur, um mehr zu erfahren.${systemOn('march') ? ' Am kleinen Griff neben einem Freund zieht ihr seine Marschrichtung für den Kampfbeginn.' : ''}</p>
         <p>Sturm: <b>${esc(stormName(S.storm))}</b>.${systemOn('retreat') ? (S.run?.noRetreat ? ' <b>Am Durchlass gibt es kein Zurück:</b> Den Boss kann man nicht überspringen, also wird bis zum Letzten gekämpft.' : ` Rückzug: <b>${esc(combatRules.retreatThresholds.find((r) => r.id === S.retreat)!.name)}</b>. Alle fliehen gemeinsam, sobald die Gegner anteilig so viel mehr Leben übrig haben als eure Freunde.`) : ''}</p>
         <p class="muted">${figuresWanted() ? 'Rote Spitzen am Boden eines Freundes: Von dort greift ihn ein Sturmwesen zuerst an.' : 'Rote Kerben über einem Freund: So viele Sturmwesen greifen ihn zuerst an.'} Zeigt auf ihn, dann seht ihr, wer.</p>
         ${guardHint()}${bondHint()}`;
    if (!hovered && S.run?.notes?.length) html += S.run.notes.map((n) => `<p class="message">${esc(n)}</p>`).join('');
    // K33 (Phase Kern): Die Tempo-Liste beim Aufstellen gehört zur Marschrichtung und ist mit ihr ausgeblendet
    if (!hovered && systemOn('march')) html += tempoList(setupTempo());
    if (S.message) html += `<p class="message">${esc(S.message)}</p>`;
  } else if (S.screen === 'battle' && S.result) {
    const focus = S.hoverUid ?? S.selectedUid;
    const u = focus !== null ? S.result.units[focus] : undefined;
    html = u
      ? infoHtml(battleInfo(u)) +
        (S.selectedUid !== null ? logHtml(S.selectedUid) : '<p class="muted">Anklicken zeigt den Verlauf dieser Figur.</p>')
      : `<h3>Kampf</h3><p>Kein Eingreifen. Zeigt auf eine Figur, um ihre Werte zu sehen, und klickt sie an für ihren Verlauf.</p><ul class="legend">
           <li><span class="sw hp"></span>Leben <span class="sw shield"></span>Schild <span class="sw mut"></span>Mut: voll heißt Kniff</li>
           ${figuresWanted() ? '<li><b class="notch">▲</b> Rote Spitzen am Boden eines Freundes: Von dort greift ihn gerade ein Sturmwesen an. Zeigt auf ihn, dann seht ihr, wer.</li><li>Der farbige Ring am Boden zeigt die Klasse. Leisten, hinter denen gekämpft wird, scheinen durch.</li>' : '<li><b class="notch">▮▮</b> Rote Kerben über einem Freund: So viele Sturmwesen greifen ihn gerade an. Zeigt auf ihn, dann seht ihr, wer.</li>'}
           <li><b class="wound-note">✚</b> Rotes Kreuz: verwundet. Fällt er noch einmal, nimmt er Abschied. Graue Figur mit Sternen: benommen.</li>
           <li><span class="num enemy-hit">48</span> Schaden an einem Gegner, <span class="num friend-hit">48</span> an einem Freund, <span class="num heal">+23</span> Heilung, <span class="num poison">15</span> Gift, <span class="num nachwirken">~12</span> wirkt nach (Waschbär)</li>
           <li><span class="crit">123!</span> Volltreffer, <span class="mark">✕</span> Schwachstelle vom Eisvogel</li>
           ${figuresWanted() ? '' : '<li>Kurze Linie: wer gerade wen trifft oder heilt</li>'}
           ${systemOn('wind') ? '<li>Helle Striche übers Feld: eine Böe schiebt eure Freunde ein Stück</li>' : ''}
           ${systemOn('lightning') ? `<li>Dunkler Kreis: dort schlägt gleich ein Blitz ein. Freunde ${systemOn('lightningDamage') ? 'nehmen Schaden und ' : ''}sind kurz benommen, Sturmwesen werden schneller</li>` : ''}
         </ul>` + tempoList(S.result.units.filter((x) => x.kind !== 'tree').map((x) => ({ name: x.side === 'enemy' ? (x.kind === 'stormling' ? enemyLabel(displayName(x.uid), x.defId) : displayName(x.uid)) : x.name, side: x.side, speed: x.moveSpeed })));
  }
  if (html !== lastSidebar) {
    // L7 (Fabi, nach Runde 8): Der Verlauf zeigt die jüngsten Einträge. Gesucht wird nur in der Seitenleiste: Im Run
    // stehen weiter vorn im Dokument andere Listen (Fluss, Dialoge), und die wurden statt des Verlaufs gerollt.
    // Wer im Verlauf derselben Figur nach oben gerollt hat, um nachzulesen, bleibt dort.
    const info = $('info');
    const old = info.querySelector<HTMLElement>('.log');
    const keep = old && S.selectedUid === lastLogUid && old.scrollTop + old.clientHeight < old.scrollHeight - 8 ? old.scrollTop : null;
    info.innerHTML = html;
    lastSidebar = html;
    lastLogUid = S.selectedUid;
    const log = info.querySelector<HTMLElement>('.log');
    if (log) {
      // Der Verlauf passt in die Seitenleiste, damit auch der jüngste Eintrag ganz zu sehen ist
      const room = info.getBoundingClientRect().bottom - parseFloat(getComputedStyle(info).paddingBottom) - log.getBoundingClientRect().top;
      if (room < log.offsetHeight) log.style.maxHeight = `${Math.max(120, Math.floor(room))}px`;
      log.scrollTop = keep ?? log.scrollHeight;
    }
  }
}

export function battleInfo(u: Unit): Info {
  if (u.kind === 'tree') return { kind: 'tree', id: 'eichhoernchen', traits: [] };
  if (u.kind === 'infested') {
    const e = S.enemies.find((x) => x.infested && x.infested.name === u.name);
    return { kind: 'enemy', id: u.defId, traits: u.traits, unit: liveUnit(u), infested: e?.infested };
  }
  if (u.side === 'friend') return { kind: 'friend', id: u.defId, traits: [], unit: liveUnit(u), member: S.roster.find((m) => m.key === u.runId || m.name === u.name) };
  return { kind: 'enemy', id: u.defId, traits: u.traits, unit: liveUnit(u) };
}

/** L7: Was dieser Figur bisher passiert ist, chronologisch, wie ein Lebenslauf im Kleinen */
/** Fabi (Phase Kern, 17.10.2026): Namen in der Chronik in der Farbe ihrer Klasse, etwas aufgehellt, damit man sie auf dem dunklen Grund liest */
function lighten(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = (shift: number) => Math.round(((n >> shift) & 255) * (1 - k) + 255 * k);
  return `rgb(${c(16)}, ${c(8)}, ${c(0)})`;
}

/**
 * Der Verlauf einer Figur im Kampf, die Chronik. Fabi (Phase Kern, 17.10.2026): farbig und mit Zeichen, damit sie sich
 * ohne genaues Lesen erschließt: Namen in der Farbe ihrer Klasse (Sturmwesen lila), Schaden an Freunden rot, Heilung
 * grün, Gift grün mit Tropfen, Kniffe golden hervorgehoben (die der Sturmwesen violett), Volltreffer orange, Schilde hellblau.
 */
export function logHtml(uid: number): string {
  const r = S.result!;
  const self = r.units[uid]!;
  const colorOf = (id: number) => {
    const u = r.units[id];
    if (!u) return '';
    if (u.kind === 'tree') return '#9fc28a';
    if (u.side === 'friend') return lighten(CLASS_COLORS[u.friendClass ?? ''] ?? '#cccccc', 0.35);
    return u.kind === 'infested' ? '#c9a6f0' : '#cfcde6';
  };
  const nm = (id: number | null, fallback: string) => (id === null ? fallback : `<span class="who" style="color:${colorOf(id)}"${speciesTitle(id)}>${displayName(id)}</span>`);
  const lines: string[] = [];
  const push = (t: number, line: string, cls = '') => lines.push(`<li${cls ? ` class="${cls}"` : ''}><span class="t">${fmt(t)} s</span> ${line}</li>`);
  // Kleine Heilungen, nachwirkender Schaden und Gift derselben Quelle kurz hintereinander zu einer Zeile zusammenfassen.
  // Nach Runde 8 (Fabi): je Quelle und Ziel getrennt, damit Gift auf mehreren Gegnern nicht den ganzen Verlauf füllt.
  // Fabi (17.10.2026): Die Zeile sagt jetzt, über wie viele Sekunden sie zusammengefasst ist
  const runs = new Map<string, { t: number; last: number; sum: number; idx: number }>();
  // Spieltest Phase 4 (Fabi): Kniff und seine Treffer in einer Zeile, „Kniff: Kieselwurf auf Schlammling: 98“
  const kniffAt = new Set(r.events.filter((e) => e.type === 'kniff' && e.unit === uid).map((e) => e.t));
  let kniffHits: { to: number; text: string }[] = [];
  const hitText = (e: { amount: number; crit: boolean; weakSpot: boolean }) =>
    `<b>${Math.round(e.amount)}</b>${e.crit ? ' <b class="lg-crit">Volltreffer!</b>' : ''}${e.weakSpot ? ' <span class="lg-mark">✕ Schwachstelle</span>' : ''}`;
  // Fabi (17.10.2026): „Trifft Dreckschleuder: 0 (60 geblockt durch Schild von Dreckschleuder 3)“
  const blockText = (blocked: number, by: number | undefined, to: number) =>
    blocked > 0 ? ` <span class="lg-block">(${blocked} geblockt durch ${by !== undefined && by !== to ? `den Schild von ${nm(by, '?')}` : 'den Schild'})</span>` : '';
  const kniffName = (via: string | undefined) => {
    const k = via?.split('|').find((v) => v.startsWith('kniff:'));
    return k ? esc(viaName(k)) : null;
  };
  for (const e of r.events) {
    if (e.t > S.playT) break;
    const runKey =
      e.type === 'heal' ? `h${e.from}>${e.to}:${e.via ?? ''}` : e.type === 'damage' && e.source === 'dot' ? `d${e.from}>${e.to}` : e.type === 'damage' && e.source === 'poison' ? `p${e.from}>${e.to}:${e.via ?? ''}` : null;
    if (runKey && e.type !== 'kniff' && ((e as { from: number | null }).from === uid || (e as { to: number }).to === uid)) {
      const ev = e as { from: number | null; to: number; amount: number; via?: string };
      const poison = e.type === 'damage' && e.source === 'poison';
      // Fabi (Phase Kern): Jede Zeile nennt ihren Grund, etwa „heilt sich mit der Holunderkette“ oder „durch das Gift von …“
      const why = ev.via ? ` (${esc(viaName(ev.via))})` : '';
      const over = (first: number, last: number) => (last > first + 1e-6 ? ` <span class="dim">in ${fmt(last - first + combatRules.poisonTickSeconds)} s</span>` : '');
      const sumText = (sum: number, first: number, last: number) =>
        e.type === 'heal'
          ? ev.from === uid && ev.to === uid
            ? `<span class="lg-heal">${ev.via === 'passive:nachwachsen' ? 'erholt sich' : 'heilt sich'}${ev.via ? ` ${esc(viaPhrase(ev.via))}` : ''}: <b>+${Math.round(sum)}</b></span>${over(first, last)}`
            : ev.from === uid
              ? `<span class="lg-heal">heilt</span> ${nm(ev.to, '?')}${why}: <b class="lg-heal">+${Math.round(sum)}</b>${over(first, last)}`
              : `wird von ${nm(ev.from, '?')} <span class="lg-heal">geheilt</span>${why}: <b class="lg-heal">+${Math.round(sum)}</b>${over(first, last)}`
          : poison
            ? ev.from === uid && ev.to !== uid
              ? `<span class="ico ico-gift"></span><span class="lg-poison">Gift</span> wirkt bei ${nm(ev.to, '?')}${why}: <b class="lg-poison">${Math.round(sum)}</b>${over(first, last)}`
              : `nimmt <b class="lg-poison">${Math.round(sum)}</b> Schaden durch das <span class="ico ico-gift"></span><span class="lg-poison">Gift</span> von ${nm(ev.from, '?')}${why}${over(first, last)}`
            : ev.from === uid
              ? `wirkt bei ${nm(ev.to, '?')} <span class="lg-dot">nach</span>: <b class="lg-dot">${Math.round(sum)}</b>${over(first, last)}`
              : `nimmt <span class="lg-dot">nachwirkend</span> <b class="lg-dot">${Math.round(sum)}</b> Schaden von ${nm(ev.from, '?')}${over(first, last)}`;
      const run = runs.get(runKey);
      if (run && e.t - run.t < 3.5) {
        run.sum += ev.amount;
        run.last = e.t;
        lines[run.idx] = `<li><span class="t">${fmt(run.t)} s</span> ${sumText(run.sum, run.t, run.last)}</li>`;
        continue;
      }
      runs.set(runKey, { t: e.t, last: e.t, sum: ev.amount, idx: lines.length });
      push(e.t, sumText(ev.amount, e.t, e.t));
      continue;
    }
    let line = '';
    let cls = '';
    switch (e.type) {
      case 'damage':
        if (e.from === uid && e.source === 'kniff' && kniffAt.has(e.t)) {
          kniffHits.push({ to: e.to, text: hitText(e) });
          break;
        }
        {
          // Nach Runde 8 und Phase Kern (Fabi): Jeder Schaden steht mit seiner Ursache da, auch Splitter, Stacheln und die eigene Moorbeere
          const why = e.via && e.source !== 'kniff' ? ` (${esc(viaName(e.via))})` : '';
          const blocked = Math.round(e.blocked ?? 0);
          const block = blockText(blocked, e.blockedBy, e.to);
          // Fabi (17.10.2026): Schaden aus dem Kniff eines anderen soll sich bedeutsam anfühlen
          const kniff = e.source === 'kniff' ? kniffName(e.via) : null;
          if (e.from === uid) {
            const verb = e.source === 'kniff' ? `<b class="lg-kniff">✦ ${kniff ?? 'Kniff'}</b> trifft` : e.source === 'splash' ? 'Splitter treffen' : e.source === 'thistle' ? 'Distelstacheln treffen' : e.source === 'reflect' ? 'Stacheln treffen' : 'trifft';
            line = `${verb} ${nm(e.to, '?')}${why}: ${hitText(e)}${block}`;
            if (e.source === 'kniff') cls = self.side === 'friend' ? 'kniff' : 'kniff-foe';
          } else if (e.to === uid) {
            const from =
              e.source === 'lightning'
                ? ' vom Blitz'
                : e.source === 'poison'
                  ? ` durch das <span class="ico ico-gift"></span><span class="lg-poison">Gift</span> von ${nm(e.from, '?')}`
                  : e.source === 'splash'
                    ? ` durch Splitter von ${nm(e.from, '?')}`
                    : e.source === 'thistle'
                      ? ` durch Distelstacheln von ${nm(e.from, '?')}`
                      : e.source === 'reflect'
                        ? ` durch die Stacheln von ${nm(e.from, '?')}`
                        : e.source === 'recoil'
                          ? ' durch die eigene Moorbeere'
                          : kniff
                            ? ` durch <b class="lg-kniff${r.units[e.from ?? -1]?.side === 'enemy' ? '-foe' : ''}">✦ ${kniff}</b> von ${nm(e.from, '?')}`
                            : ` von ${nm(e.from, '?')}`;
            const amount = `<b class="${self.side === 'friend' ? 'lg-dmg' : 'lg-hurt'}">${Math.round(e.amount)}</b>`;
            line = `nimmt ${amount} Schaden${from}${e.source === 'recoil' ? '' : why}${e.crit ? ' <b class="lg-crit">Volltreffer!</b>' : ''}${block}`;
            if (kniff) cls = r.units[e.from ?? -1]?.side === 'enemy' ? 'kniff-foe' : 'kniff';
          }
        }
        break;
      case 'spores':
        if (e.unit === uid) line = '<b>Der Staubpilz platzt!</b> Die Sporenwolke nebelt alle ein, die ihn gerade angreifen';
        break;
      case 'poisonJump':
        if (e.source === uid) line = `<span class="ico ico-gift"></span><span class="lg-poison">Gift springt</span> von ${nm(e.from, '?')} auf ${nm(e.to, '?')} über (${e.stacks} Stapel)`;
        else if (e.from === uid) line = `das <span class="lg-poison">Gift springt</span> auf ${nm(e.to, '?')} über`;
        else if (e.to === uid) line = `<span class="lg-poison">Gift springt</span> von ${nm(e.from, '?')} über (${e.stacks} Stapel)`;
        break;
      case 'heal':
        // läuft oben über die Zusammenfassung; hier nur der Vollständigkeit halber
        break;
      case 'kniff':
        if (e.unit === uid) {
          const head = `<b class="lg-kniff${self.side === 'enemy' ? '-foe' : ''}">✦ Kniff: ${esc(getAbility(e.kniff).name)}</b>`;
          const hits = kniffHits;
          kniffHits = [];
          if (hits.length === 1 && (e.target === null || hits[0]!.to === e.target)) line = `${head} auf ${nm(hits[0]!.to, '?')}: ${hits[0]!.text}`;
          else if (hits.length > 1) line = `${head}: ${hits.map((h) => `${nm(h.to, '?')} ${h.text}`).join(', ')}`;
          else line = `${head}${e.target !== null ? ` auf ${nm(e.target, '?')}` : ''}`;
          cls = self.side === 'enemy' ? 'kniff-foe' : 'kniff';
        }
        break;
      case 'mark':
        if (e.by === uid) line = `macht bei ${nm(e.to, '?')} eine <span class="lg-mark">✕ Schwachstelle</span> aus`;
        else if (e.to === uid) line = 'hat jetzt eine <span class="lg-mark">✕ Schwachstelle</span>';
        break;
      case 'status':
        // Fabi (Phase Kern): Jeder Zustand mit seiner Herkunft, etwa „Heißhunger: macht eine Weile mehr Schaden“
        if (e.unit === uid && STATUS_TEXT[e.status]) line = `${e.via ? `${esc(viaName(e.via))}: ` : ''}${STATUS_TEXT[e.status]}`;
        break;
      case 'move':
        if (e.unit === uid) line = e.reason === 'leap' ? 'springt' : e.reason === 'return' ? 'springt zurück' : e.reason === 'scared' ? 'erschrickt und weicht zurück' : 'wird weggeschubst';
        break;
      case 'down':
        // F42 (Fabi, Phase Kern): Wer zum ersten Mal fällt, kommt verwundet zurück; wer verwundet war, nimmt Abschied
        if (e.unit === uid) {
          line = self.kind === 'tree' ? '<b>fällt</b>' : self.side === 'friend' ? (self.wounded ? '<b class="lg-dmg">nimmt Abschied</b>' : '<span class="ico ico-wunde"></span><b class="lg-dmg">fällt</b> und kommt verwundet zurück an Bord') : '<b>vertrieben</b>';
          cls = 'down';
        } else if (e.by === uid) line = `<b>vertreibt</b> ${nm(e.unit, '?')}`;
        break;
      case 'pushedOut':
        if (e.unit === uid) line = '<b>über den Rand geschubst</b>, wartet dort bis nach dem Kampf';
        break;
      case 'escape':
        if (e.unit === uid) line = self.side === 'friend' ? '<b>in Sicherheit</b>' : '<b>entkommen</b>';
        break;
      default:
        break;
    }
    if (line) push(e.t, line, cls);
  }
  return `<h4>Verlauf</h4><ol class="log">${lines.join('') || '<li class="muted">Noch nichts passiert.</li>'}</ol>`;
}

export const STATUS_TEXT: Record<string, string> = {
  rolled: 'rollt sich ein',
  taunted: 'greift eine Weile nur den Igel an',
  slowed: 'wird langsamer',
  hasted: 'wird schneller',
  guarded: 'nimmt eine Weile weniger Schaden',
  empowered: 'macht eine Weile mehr Schaden',
  shielded: 'bekommt einen Schild',
  spiky: 'stellt die Stacheln auf',
  spinning: 'kreiselt los',
  burrowed: 'gräbt sich ein',
  toxic: 'hat eine Weile giftige Haut',
  fleeing: 'flieht!',
  enraged: 'wird wütend',
  stunned: 'ist kurz benommen',
  retreating: '<b>zieht sich zurück</b>',
  blinded: 'hat Dreck in den Augen und trifft ein paar Angriffe lang schwächer',
  timid: 'schreckt zurück und greift ein paar Angriffe lang zaghaft an',
  distracted: 'ist abgelenkt',
  confused: 'vergisst seinen Kniff',
  climbing: 'vom Baum gepustet, klettert wieder hinauf',
  guarding: 'eilt zu seinem Schützling',
  amber: 'wird in Bernstein gehüllt: Eine Weile tut ihm kein Treffer weh',
};

/** G30: Beim Darüberfahren über einen Spitznamen in der Chronik steht die Spezies */
function speciesTitle(uid: number): string {
  const u = S.result?.units[uid];
  return u && u.kind === 'stormling' ? ` title="${esc(getEnemy(u.defId).name)}"` : '';
}

export function displayName(uid: number): string {
  const r = S.result!;
  const u = r.units[uid]!;
  if (u.kind === 'tree') return esc(u.name);
  if (u.side === 'enemy') {
    if (u.kind === 'infested') return esc(`${u.name} (befallen)`);
    const enemies = r.units.filter((x) => x.side === 'enemy' && x.kind === 'stormling');
    return esc(enemyNames(enemies.map((x) => x.defId))[enemies.indexOf(u)]!);
  }
  return esc(u.name);
}

/** Werte eines Sim-Wesens zum aktuellen Abspielzeitpunkt, für die Seitenleiste */
export function liveUnit(u: Unit): Unit {
  const f = currentFrame();
  const d = f?.units[u.uid];
  return d ? { ...u, hp: d[2], mut: d[3], shield: d[7] ?? 0 } : u;
}

export const liveMut = (u: Unit) => u.mut;

/** Seitenleiste beim nächsten Bild neu aufbauen */
export function forgetSidebar(): void {
  lastSidebar = '';
}
